import { TIMEOUT_5000_MS } from '@base-shared/utils/constants';

export type JSONPrimitive = string | number | boolean | null | undefined;

export type JSONValue = JSONPrimitive | JSONValue[] | {
  [key: string]: JSONValue;
};

export const MESSAGE_TYPES = {
  PING_PONG: 'spx:ping-pong',
  STORAGE_ACCESS: 'spx:storage-access-request',
  CHANGE_LANGUAGE: 'spx:change-language',
  GET_AUTH_SESSION: 'spx:get-auth-session',
  SET_AUTH_SESSION: 'spx:set-auth-session'
};

export type MessageType = typeof MESSAGE_TYPES[keyof typeof MESSAGE_TYPES];
export interface EventData {
  messageType: MessageType;
  payload: Record<string, string>;
}

export const useHubAuth = (iframeSrc: string) => {
  const origin = new URL(iframeSrc).origin;
  const listeners = new Map<string, [MessageType, (event: MessageEvent<EventData>) => void]>();
  let iframe: HTMLIFrameElement | null = null;

  /**
   * Add an event listener for a specific message type.
   */
  const addEventListener = (handlerId: string, messageType: MessageType, handler: (event: MessageEvent<EventData>) => void) => {
    if (!listeners.has(handlerId)) {
      listeners.set(handlerId, [messageType, handler]);
    }
  };

  /**
   * Remove an event listener for a specific listener ID.
   */
  const removeEventListener = (handlerId: string) => {
    if (listeners.has(handlerId)) {
      listeners.delete(handlerId);
    }
  };

  /**
   * This function sends a message to the iframe.
   * @param messageType The type of message to send.
   * @param payload The payload to send with the message.
   */
  const sendMessage = (messageType: MessageType, payload: Record<string, string>) => {
    if (!iframe || !iframe.contentWindow) {
      throw new Error('HUB/Auth iframe is not initialized');
    }

    console.log('Sending message to iframe:', messageType, payload);
    iframe.contentWindow.postMessage({
      messageType,
      payload
    },
    origin
    );
  };

  /**
   * This function waits for a specific message type from the iframe for `timeout` milliseconds.
   */
  const waitForResponseWithTimeout = (messageType: MessageType, payload: Record<string, string>, timeout: number): Promise<MessageEvent<EventData>> => {
    const listenerId = `listener-${messageType}-${Date.now()}`;
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line prefer-const
      let timeoutId: ReturnType<typeof setTimeout>;
      const messageHandler = (event: MessageEvent<EventData>) => {
        // Clear the timeout as we received the message
        clearTimeout(timeoutId);
        removeEventListener(listenerId);
        resolve(event);
      };

      timeoutId = setTimeout(() => {
        removeEventListener(listenerId);
        reject(new Error(`Message reception timed out after ${timeout}ms`));
      }, timeout);

      // send message to iframe to request for a session
      addEventListener(listenerId, messageType, messageHandler);
      sendMessage(messageType, {});
    });
  };

  const waitForResponse = (messageType: MessageType, payload: Record<string, string> = {}): Promise<MessageEvent<EventData>> => {
    const listenerId = `listener-${messageType}-${Date.now()}`;
    return new Promise((resolve) => {
      const messageHandler = (event: MessageEvent<EventData>) => {
        // Clear the timeout as we received the message
        removeEventListener(listenerId);
        resolve(event);
      };

      // send message to iframe to request for a session
      addEventListener(listenerId, messageType, messageHandler);
      sendMessage(messageType, payload);
    });
  };

  /**
   * This function listens for messages from the iframe and handles them accordingly.
   * @param event The event object from the message listener.
   */
  const messageListener = async (event: MessageEvent<EventData>) => {
    console.log('Received message from iframe:', event.data, event.origin);
    if (!origin.includes(event.origin)) {
      console.warn('Received message from an untrusted origin:', event.origin);
      return; // Validate origin
    }

    // Iterate over all registered listeners and find the appropriate handler
    for (const [, handlerPair] of listeners) {
      const [messageType, messageHandler] = handlerPair;
      if (event.data.messageType === messageType) {
        if (messageHandler) {
          messageHandler(event);
        }
      }
    }
  };

  /**
   * Resets the Hub authentication state.
   * This function removes the window message listener, clears all registered listeners,
   */
  const reset = () => {
    if (iframe) {
      window.removeEventListener('message', messageListener);
    }

    listeners.clear();
    iframe = null;
  };

  /**
   * Initializes the iframe for Hub authentication.
   * This function sets up the iframe element, checks if it has already been initialized,
   * @returns Promise<void>
   */
  const init = async (srcIframe: HTMLIFrameElement, timeout = TIMEOUT_5000_MS) => {
    if (!srcIframe) {
      throw new Error('HUB/Auth iframe not found');
    }

    iframe = srcIframe;
    window.addEventListener('message', messageListener);

    return new Promise<void>((resolve, reject) => {
      // Check if the iframe is initialized via ping/pong mechanism
      const loadHandler = () => {
        const handler = async () => {
          await waitForResponseWithTimeout(MESSAGE_TYPES.PING_PONG, { ping: 'ping' }, timeout);
          resolve();
        };

        handler().catch(() => {
          reset();
          reject(new Error('HUB/Auth is not responding'));
        });
      };

      const loadErrorHandler = () => {
        reset();
        reject(new Error('Failed to load HUB/Auth iframe'));
      };

      srcIframe.addEventListener('load', loadHandler, true);
      srcIframe.addEventListener('error', loadErrorHandler, true);
    });
  };

  /**
   * Retrieves the authentication session from the iframe (Object) or null
   */
  const retrieveAuthSession = async (timeout = TIMEOUT_5000_MS) => {
    const userSession = await waitForResponseWithTimeout(
      MESSAGE_TYPES.GET_AUTH_SESSION, {}, timeout
    );

    if (userSession && userSession.data) {
      try {
        return JSON.parse(userSession.data?.payload?.session ?? 'null');
      } catch (error) {
        console.warn('Failed to parse auth session data:', userSession.data, error);
        return null;
      }
    }

    return null;
  };

  /**
   *
   * This function sends a message to the iframe to set the authentication session.
   */
  // FIXME: use string instead of Record<string, any> here because we shouldn't care about type here
  const updateAuthSession = (session: JSONValue) => {
    sendMessage(
      MESSAGE_TYPES.SET_AUTH_SESSION,
      { session: JSON.stringify(session) }
    );
  };

  /**
   * Set i18n language in the Hub iframe.
   */
  const setLanguage = (language: string) => {
    sendMessage(MESSAGE_TYPES.CHANGE_LANGUAGE, { language });
  };

  /**
   * Requests storage access from the user.
   * This function waits for a response from the iframe indicating whether storage access was granted.
   * If the user grants access, it resolves; otherwise, it resets the state and throws an error.
   */
  const requestForStorageAccess = async () => {
    try {
      const response = await waitForResponse(MESSAGE_TYPES.STORAGE_ACCESS);
      console.log('Storage access response:', response.data);
      if (response.data.payload?.enabled !== 'true') {
        reset();
        throw new Error('Storage access not granted by the user, HUB/Auth is disabled');
      }
    } catch (error) {
      console.warn('Failed to request storage access:', error);
    }
  };

  return {
    // low-level APIs
    sendMessage,
    addEventListener,
    removeEventListener,
    waitForResponse,
    waitForResponseWithTimeout,

    // external APIs
    init,
    reset,
    isHubEnabled: () => !!iframe,
    retrieveAuthSession,
    updateAuthSession,
    setLanguage,
    requestForStorageAccess
  };
};
